// Requests go to the same origin by default. In development the webpack dev
// server proxies `/api` to the local auth-server; in production a reverse proxy
// serves both the GUI and `/api`, so sessions stay first-party cookies and work
// for localhost as well as remotely published previews.
const DEFAULT_AUTH_API_BASE_URL = '/api/auth';
const ADMIN_ROLE = 'admin';
const DEFAULT_ROLE = 'user';

// A hung authentication request would otherwise leave the dialog stuck in its
// "working" state forever, so every request gets a hard deadline.
const REQUEST_TIMEOUT_MS = 15000;

const createAuthError = function (code) {
    const error = new Error(code);
    error.code = code;
    return error;
};

// The server always answers with {error: {code, message}}. Keeping both lets a
// failure explain itself even when the code is one the UI does not know yet,
// instead of collapsing into a generic "request failed" message.
const createAuthErrorFromResponse = function (response) {
    return response.json()
        .then(payload => {
            const serverError = payload && payload.error;
            if (serverError && serverError.code) {
                const error = createAuthError(serverError.code);
                error.status = response.status;
                error.serverMessage = serverError.message || '';
                return error;
            }
            const error = createAuthError('AUTH_REQUEST_FAILED');
            error.status = response.status;
            return error;
        })
        // Our API always answers errors with a JSON body. A body that is not
        // JSON therefore means the request never reached it: the dev server
        // proxy or a reverse proxy produced an HTML error page because the
        // authentication service was not running. Reporting that as an
        // unreachable service is far more actionable than a generic failure.
        .catch(() => createAuthError('NETWORK_ERROR'));
};

const getAuthApiBaseUrl = function () {
    if (typeof window !== 'undefined' && window.__OPENBLOCK_AUTH_API_BASE_URL__) {
        return window.__OPENBLOCK_AUTH_API_BASE_URL__.replace(/\/$/, '');
    }
    return DEFAULT_AUTH_API_BASE_URL;
};

const toUser = function (payload) {
    const user = payload && payload.user;
    if (!user || !user.id || !user.displayName) {
        throw createAuthError('INVALID_AUTH_RESPONSE');
    }
    return {
        id: String(user.id),
        account: user.account ? String(user.account) : '',
        displayName: String(user.displayName),
        avatarUrl: user.avatarUrl ? String(user.avatarUrl) : null,
        // Anything that is not explicitly the administrator role is an
        // ordinary user, so an unexpected value can never unlock the screen.
        role: user.role === ADMIN_ROLE ? ADMIN_ROLE : DEFAULT_ROLE
    };
};

const request = function (path, options, baseUrl) {
    const supportsAbort = typeof AbortController !== 'undefined';
    const controller = supportsAbort ? new AbortController() : null;
    let timeoutId = null;
    if (controller) {
        timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    }
    const requestOptions = Object.assign({
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        }
    }, options, controller ? {signal: controller.signal} : {});
    const clearRequestTimeout = () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
    return fetch(`${baseUrl || getAuthApiBaseUrl()}${path}`, requestOptions)
        .then(response => {
            if (response.ok) {
                return response.status === 204 ? null : response.json();
            }
            return createAuthErrorFromResponse(response).then(error => {
                throw error;
            });
        })
        .catch(error => {
            // A deliberate auth failure (401/409/429...) must keep its own code;
            // everything else is reported as an unreachable-service problem.
            if (error && error.code) {
                throw error;
            }
            throw createAuthError('NETWORK_ERROR');
        })
        .then(result => {
            clearRequestTimeout();
            return result;
        }, error => {
            clearRequestTimeout();
            throw error;
        });
};

const login = function ({account, password}) {
    return request('/login', {
        method: 'POST',
        body: JSON.stringify({account: account, password: password})
    }).then(toUser);
};

const register = function ({account, password, displayName}) {
    return request('/register', {
        method: 'POST',
        body: JSON.stringify({
            account: account,
            password: password,
            displayName: displayName
        })
    }).then(toUser);
};

const fetchCurrentUser = function () {
    return request('/me', {
        method: 'GET'
    }).then(toUser);
};

const logout = function () {
    return request('/logout', {
        method: 'POST'
    });
};

// The auth routes live under /api/auth, but the project routes live beside them
// at /api/projects. Both are proxied by the same `/api` rule, so the project
// requests swap the auth suffix for the project one instead of appending to it.
const PROJECTS_PATH = '/projects';

const getProjectsBaseUrl = function () {
    const authBase = getAuthApiBaseUrl();
    return authBase.replace(/\/auth$/, '');
};

const requestProject = function (path, options) {
    return request(path, options, getProjectsBaseUrl());
};

const listProjects = function () {
    return requestProject(PROJECTS_PATH, {method: 'GET'}).then(payload => (
        (payload && payload.projects) || []
    ));
};

const fetchProject = function (id) {
    return requestProject(`${PROJECTS_PATH}/${encodeURIComponent(id)}`, {method: 'GET'})
        .then(payload => payload && payload.project);
};

const createProject = function ({name, content, encoding}) {
    return requestProject(PROJECTS_PATH, {
        method: 'POST',
        body: JSON.stringify({name: name, content: content, encoding: encoding})
    }).then(payload => payload && payload.project);
};

const updateProject = function (id, updates) {
    const body = {};
    if (Object.prototype.hasOwnProperty.call(updates, 'name')) body.name = updates.name;
    if (Object.prototype.hasOwnProperty.call(updates, 'content')) {
        body.content = updates.content;
        body.encoding = updates.encoding;
    }
    return requestProject(`${PROJECTS_PATH}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body)
    }).then(payload => payload && payload.project);
};

const deleteProject = function (id) {
    return requestProject(`${PROJECTS_PATH}/${encodeURIComponent(id)}`, {method: 'DELETE'});
};

const updateProfile = function ({displayName}) {
    return request('/profile', {
        method: 'PATCH',
        body: JSON.stringify({displayName: displayName})
    }).then(toUser);
};

const changePassword = function ({currentPassword, newPassword}) {
    return request('/password', {
        method: 'POST',
        body: JSON.stringify({
            currentPassword: currentPassword,
            newPassword: newPassword
        })
    });
};

// ------------------------------------------------------------------ admin
const ADMIN_PATH = '/admin';

const getAdminBaseUrl = function () {
    // Admin routes sit beside /api/auth and /api/projects, so the auth suffix is
    // swapped rather than appended to.
    return getAuthApiBaseUrl().replace(/\/auth$/, '');
};

const requestAdmin = function (path, options) {
    return request(path, options, getAdminBaseUrl());
};

const toAdminUser = user => ({
    id: String(user.id),
    account: user.account,
    displayName: user.displayName,
    role: user.role === ADMIN_ROLE ? ADMIN_ROLE : DEFAULT_ROLE,
    disabled: user.disabled === true,
    projectCount: typeof user.projectCount === 'number' ? user.projectCount : null,
    createdAt: user.createdAt || null
});

const fetchAdminOverview = function () {
    return requestAdmin(`${ADMIN_PATH}/overview`, {method: 'GET'})
        .then(payload => (payload && payload.overview) || null);
};

const listAdminUsers = function () {
    return requestAdmin(`${ADMIN_PATH}/users`, {method: 'GET'}).then(payload => (
        ((payload && payload.users) || []).map(toAdminUser)
    ));
};

const listAdminProjects = function () {
    return requestAdmin(`${ADMIN_PATH}/projects`, {method: 'GET'}).then(payload => (
        ((payload && payload.projects) || []).map(project => ({
            id: String(project.id),
            name: project.name,
            size: project.size,
            ownerId: String(project.ownerId),
            ownerAccount: project.ownerAccount || null,
            updatedAt: project.updatedAt || null
        }))
    ));
};

const updateAdminUser = function (id, updates) {
    const body = {};
    if (Object.prototype.hasOwnProperty.call(updates, 'disabled')) body.disabled = updates.disabled;
    if (Object.prototype.hasOwnProperty.call(updates, 'role')) body.role = updates.role;
    if (Object.prototype.hasOwnProperty.call(updates, 'displayName')) {
        body.displayName = updates.displayName;
    }
    return requestAdmin(`${ADMIN_PATH}/users/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
    }).then(payload => payload && toAdminUser(payload.user));
};

const resetAdminUserPassword = function (id, password) {
    return requestAdmin(`${ADMIN_PATH}/users/${encodeURIComponent(id)}/password`, {
        method: 'POST',
        body: JSON.stringify({password: password})
    });
};

const deleteAdminUser = function (id) {
    return requestAdmin(`${ADMIN_PATH}/users/${encodeURIComponent(id)}`, {method: 'DELETE'});
};

const deleteAdminProject = function (id) {
    return requestAdmin(`${ADMIN_PATH}/projects/${encodeURIComponent(id)}`, {method: 'DELETE'});
};

export {
    ADMIN_ROLE,
    changePassword,
    deleteAdminProject,
    deleteAdminUser,
    fetchAdminOverview,
    listAdminProjects,
    listAdminUsers,
    resetAdminUserPassword,
    updateAdminUser,
    createProject,
    deleteProject,
    fetchCurrentUser,
    fetchProject,
    listProjects,
    login,
    logout,
    register,
    updateProfile,
    updateProject
};
